import { useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendMessage, sendGuestMessage, sendIncognitoMessage, editMessage, regenerateMessage as regenerateMessageAPI, answerQuestion as answerQuestionAPI, uploadDocument } from '../../../services/api';
import { AskUserAnswer, findAskUserCard, markAskUserAnswered } from '../../../helpers/askUser';
import { INCOGNITO_CHAT_ID } from '../../../contexts/AppDataContext';
import { TextFileRecord } from '../../../types/files';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { useChatContext } from '../../../contexts/AppDataContext';
import { useModelContext } from '../../../contexts/ModelContext';
import { useResourceContext } from '../../../contexts/ResourceContext';
import { useAttachmentContext } from '../../../contexts/AttachmentContext';
import { useInputContext } from '../../../contexts/InputContext';
import { useMessageOperationsContext } from '../../../contexts/MessageOperationsContext';
import { useMode } from '../../../contexts/ModeContext';
import { useStreamProcessor } from '../../../hooks/useStreamProcessor';
import { useUnifiedData } from '../../../contexts/AppDataContext';
import { MessageDocument, MessageImage, DocumentType, ImageType, ConvexMessage, ClientMessage } from '../../../types/message';
import { Model } from '../../../types/model';
import { requestModel } from '../../../services/api';
import { Mode } from '../../../types/mode';
import { convexMessagesToClientMessages, withoutUnansweredTurns } from '../utils';
import { joinQuotedMessage, splitQuotedMessage } from '../../../helpers/quotedMessage';
import { useAnalytics } from '../../../hooks/useAnalytics';


/** The server answers 429 with a stable code when a message allowance is used up. */
const MESSAGE_LIMIT_CODES = new Set(['message_limit_reached', 'guest_limit_reached']);
const isMessageLimitError = (error: any): boolean =>
  error?.status === 429 && MESSAGE_LIMIT_CODES.has(error?.code);

export interface SubmitEvent {
  preventDefault: () => void;
  editContent?: string;
  editMessage?: ConvexMessage;
  editMode?: Mode;
  editModel?: Model | null;
  editImages?: MessageImage[];
  editTextAttachments?: MessageDocument[];
  /** When set, used instead of composer `input` (avoids stale closure from programmatic submit). */
  overrideInput?: string;
  /** When set, used instead of `quotedContent` for `<quote>` wrapping (avoids stale closure). */
  overrideQuotedContent?: string;
}

interface UseMessageSubmissionProps {
  scrollToBottomGenerous: () => void;
  resetHistory: () => void;
  checkMobile: () => void;
  focusInput: () => void;
}

export const useMessageSubmission = ({
  scrollToBottomGenerous,
  resetHistory,
  checkMobile,
  focusInput
}: UseMessageSubmissionProps) => {
  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { mode } = useMode();
  const { trackMessageSent, trackMessageReceived, trackMessageEdit, trackMessageRegenerate, trackChatCreated } = useAnalytics();

  const {
    isSpaceChat,
    globalBranchCount,
    setGlobalBranchCount,
    filteredMessages,
    maxBranchId,
    currentChatId,
    useReasoning,
    useWeb,
    setUseWeb,
    webCapable,
    resourceCapable,
    quotedContent,
    setQuotedContent,
    setIsSubmitting,
    isGeneratingResponse,
    setIsStreaming,
    isIncognito,
  } = useChatContext();
  const messagesHaveImages = filteredMessages.some((message) => Array.isArray(message.images) && message.images.length > 0);

  const { checkModelCompatibility, currentModel } = useModelContext();
  const { parseAndAddTextAttachments, expandedResource, isResourcesVisible } = useResourceContext();
  const {
    textFiles,
    imageFiles,
    setTextFiles,
    setImageFiles,
    isTextAttachmentViewOpen,
  } = useAttachmentContext();
  const {
    input,
    restoreInput
  } = useInputContext();

  const {
    insertNewMessage,
    removeMessage,
    markMessageasStopped,
    updateMessageChatId,
    getMessagePairId,
    stopGeneration,
    setMessages
  } = useMessageOperationsContext();

  const { addNewChat } = useUnifiedData();
  const {
    processStream,
    reset: resetThinking,
    startThinking
  } = useStreamProcessor();

  const abortControllerRef = useRef<AbortController | null>(null);
  // The chatId the in-flight stream belongs to (null when idle).
  const streamChatIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const blobCleanupTimeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set());

  // Cleanup AbortController and blob URLs on unmount
  useEffect(() => {
    const timeouts = blobCleanupTimeoutsRef.current;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
      
      // Clear all pending blob cleanup timeouts and revoke URLs immediately
      timeouts.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      timeouts.clear();
    };
  }, []);

  // Abort the in-flight stream when the user switches to a different chat
  // (no unmount happens between /chatA and /chatB). Comparing against
  // streamChatIdRef keeps the new-chat send flow's own navigation unaborted.
  const prevChatIdRef = useRef(currentChatId);
  useEffect(() => {
    const prev = prevChatIdRef.current;
    prevChatIdRef.current = currentChatId;
    if (prev === currentChatId) return;
    const controller = abortControllerRef.current;
    if (!controller || controller.signal.aborted) return;
    if (streamChatIdRef.current && streamChatIdRef.current === currentChatId) return;
    controller.abort();
    abortControllerRef.current = null;
    streamChatIdRef.current = null;
    resetThinking();
  }, [currentChatId, resetThinking]);

  const generateTempId = useCallback((): string => {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 11);
    return `temp-${timestamp}-${randomString}`;
  }, []);

  const handleDailyLimitError = useCallback(() => {
    if (auth.user) {
      toast('Monthly message limit reached', 'error');
      auth.reduceUsageData(0);
    } else {
      toast('Sign in to continue', 'error');
      auth.reduceUsageData(0);
    }
  }, [auth, toast]);

  // Incognito and guest chats have no server-side chat: edit and regenerate
  // build their branch purely in local state and stream through the stateless
  // endpoints. Those append `content` as the user turn themselves, so
  // `context` must stop before the pair being answered.
  const isLocalOnlyChat = isIncognito || !auth.user;

  const sendLocalOnlyMessage = useCallback(async (req: {
    content: string;
    mode: Mode;
    context: ClientMessage[];
    imageFiles: MessageImage[];
    textFiles: MessageDocument[];
    requestedModel: requestModel;
    signal: AbortSignal;
  }) => {
    const context = withoutUnansweredTurns(req.context);
    if (auth.user) {
      const { stream } = await sendIncognitoMessage({
        content: req.content,
        mode: req.mode,
        abortSignal: req.signal,
        imageFiles: req.imageFiles,
        textFiles: req.textFiles,
        useReasoning,
        useWeb,
        webCapable,
        resourceCapable,
        requestedModel: req.requestedModel,
        context
      });
      setUseWeb(false);
      return stream;
    }
    const { stream } = await sendGuestMessage(context, req.content, req.mode, req.signal);
    return stream;
  }, [auth.user, useReasoning, useWeb, setUseWeb, webCapable, resourceCapable]);

  /** Messages on the active branch that precede the given pair. */
  const contextBeforePair = useCallback((messagePairId: string | undefined): ClientMessage[] => {
    const index = filteredMessages.findIndex((message) => message.messagePairId === messagePairId);
    return convexMessagesToClientMessages(index === -1 ? filteredMessages : filteredMessages.slice(0, index));
  }, [filteredMessages]);

  const regenerateMessage = useCallback(async (messageId: string, mode: Mode): Promise<void> => {
    if (!isLocalOnlyChat && !currentChatId) {
      throw new Error('Current chat ID is not available');
    }
    auth.checkUsageData();
    // Use global compatibility check for regenerate
    if (!checkModelCompatibility(!!imageFiles?.length, useWeb, messagesHaveImages)) {
      return;
    }
    const requestedModel: requestModel = { id: currentModel.id };

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    streamChatIdRef.current = currentChatId ?? null;
    const messagePairId = getMessagePairId?.(messageId) || "";

    if(!messagePairId) {
      return;
    }
    let tempAiMessageId = generateTempId();
    const newBranchId = (maxBranchId || 1) + 1;
    await insertNewMessage({
      isAi: true,
      messagePairId: messagePairId,
      branchId: newBranchId,
      _id: tempAiMessageId,
      content: "",
      mode: mode
    });
    setGlobalBranchCount(newBranchId);
    const context: ClientMessage[] = convexMessagesToClientMessages(filteredMessages)
    scrollToBottomGenerous();
    
    // Track message regeneration
    trackMessageRegenerate({
      chatId: currentChatId ?? undefined,
      messageId,
      mode: mode.name || mode.id
    });
    
    const startTime = Date.now();
    try {
      startThinking();
      let stream: any;
      if (isLocalOnlyChat) {
        // Re-ask the pair's user turn with the branch before it as context.
        const userMessage = filteredMessages.find((message) => !message.isAi && message.messagePairId === messagePairId);
        if (!userMessage) {
          throw new Error('Could not find the prompt to regenerate.');
        }
        // Stored content carries the local <|endofinput|> transport suffix;
        // the server rebuilds attachment markers from textFiles.
        const { quotedText, messageText } = splitQuotedMessage(userMessage.content);
        stream = await sendLocalOnlyMessage({
          content: joinQuotedMessage(quotedText, messageText),
          mode,
          context: contextBeforePair(messagePairId),
          imageFiles: userMessage.images || [],
          textFiles: userMessage.textAttachments || [],
          requestedModel,
          signal: abortController.signal
        });
      } else {
        ({ stream } = await regenerateMessageAPI({
          chatId: currentChatId!,
          messageId,
          mode,
          globalBranchCount: newBranchId,
          useReasoning,
          useWeb,
          webCapable,
          resourceCapable,
          requestedModel,
          context,
          signal: abortController.signal
        }));
      }
      
      const asyncStream = Array.isArray(stream) ? stream[0] : stream;
      await processStream(asyncStream, tempAiMessageId, '', (newAiId: string) => {
        tempAiMessageId = newAiId;
      });
      
      // Track successful regeneration response
      const duration = Date.now() - startTime;
      trackMessageReceived({
        chatId: currentChatId ?? undefined,
        mode: mode.name || mode.id,
        responseLength: 0, // Will be updated by stream processor
        duration,
        modelId: requestedModel?.id,
        wasStoppedEarly: false
      });
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        if (isMessageLimitError(error)) {
          handleDailyLimitError();
        } else {
          toast(error.message || 'Something went wrong', 'error');
          stopGeneration(tempAiMessageId);
        }
        // No server copy will replace a failed local-only reply. Drop the
        // placeholder so the pair falls back to its previous answer instead
        // of an empty branch that later strips the question from context.
        if (isLocalOnlyChat) {
          removeMessage(tempAiMessageId);
        }
      }
    } finally {
      resetThinking();
      abortControllerRef.current = null;
      streamChatIdRef.current = null;

      if(!auth.user){
        stopGeneration(tempAiMessageId)
      }

      setIsStreaming(false);
      setIsSubmitting(false);
      
      if (isMountedRef.current) {
        setTimeout(() => {
          focusInput();
        }, 100);
      }
    }
  }, [
    setGlobalBranchCount,
    maxBranchId,
    insertNewMessage,
    scrollToBottomGenerous,
    processStream,
    getMessagePairId,
    useReasoning,
    useWeb,
    webCapable,
    resourceCapable,
    startThinking,
    currentChatId,
    stopGeneration,
    auth,
    imageFiles,
    messagesHaveImages,
    checkModelCompatibility,
    currentModel,
    resetThinking,
    generateTempId,
    toast,
    filteredMessages,
    handleDailyLimitError,
    setIsStreaming,
    setIsSubmitting,
    focusInput,
    trackMessageReceived,
    trackMessageRegenerate,
    isLocalOnlyChat,
    sendLocalOnlyMessage,
    contextBeforePair,
    removeMessage
  ]);

  /**
   * Answers an `ask_user` card and streams the rest of that answer into the
   * same message. Resolves false when nothing was sent, so the card stays
   * answerable.
   */
  const answerQuestion = useCallback(async (messageId: string, cardId: string, answers: AskUserAnswer[]): Promise<boolean> => {
    if (isLocalOnlyChat || !currentChatId) return false;
    if (isGeneratingResponse || abortControllerRef.current) {
      toast('Wait for the current reply to finish', 'error');
      return false;
    }
    const message = filteredMessages.find(m => m._id === messageId);
    const card = message ? findAskUserCard(message.content, cardId) : null;
    if (!message || !card || card.status !== 'pending') {
      toast('This question can no longer be answered', 'error');
      return false;
    }

    const originalContent = message.content;
    // What the server saves on accepting the answer; the stream appends to it.
    const answeredContent = markAskUserAnswered(originalContent, card, answers);
    const restoreCard = () => setMessages(prev => prev.map(m => (
      m._id === messageId && m.content === answeredContent
        ? { ...m, content: originalContent, isGenerating: false }
        : m
    )));

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    streamChatIdRef.current = currentChatId;
    setMessages(prev => prev.map(m => (
      m._id === messageId ? { ...m, content: answeredContent, isGenerating: true } : m
    )));
    setIsSubmitting(true);

    let accepted = false;
    try {
      const { stream } = await answerQuestionAPI({
        chatId: currentChatId,
        messageId,
        toolCallId: card.id,
        answers,
        mode: message.mode || mode,
        globalBranchCount,
        useWeb,
        webCapable,
        resourceCapable,
        requestedModel: { id: currentModel.id },
        signal: abortController.signal
      });
      accepted = true;
      startThinking();
      const asyncStream = Array.isArray(stream) ? stream[0] : stream;
      await processStream(asyncStream, messageId, '', undefined, answeredContent);
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        if (!accepted) restoreCard();
        // A 409 carries the reason: answered elsewhere, or the paused reply is still saving.
        toast(
          accepted ? 'The reply was interrupted' : (error?.message || 'Could not send your answer'),
          'error'
        );
      } else if (!accepted) {
        restoreCard();
      }
    } finally {
      resetThinking();
      abortControllerRef.current = null;
      streamChatIdRef.current = null;
      setIsStreaming(false);
      setIsSubmitting(false);
    }
    return accepted;
  }, [
    isLocalOnlyChat,
    currentChatId,
    isGeneratingResponse,
    filteredMessages,
    setMessages,
    setIsSubmitting,
    setIsStreaming,
    mode,
    globalBranchCount,
    useWeb,
    webCapable,
    resourceCapable,
    currentModel,
    startThinking,
    processStream,
    resetThinking,
    toast
  ]);

  const handleStopStreaming = useCallback(() => {
    if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
      const lastMessage = filteredMessages.find(msg => msg.isGenerating);
      
      if (filteredMessages.length > 0 && lastMessage) {
        markMessageasStopped(lastMessage);
      }

      setIsSubmitting(false);
      setIsStreaming(false);
      
      try {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        streamChatIdRef.current = null;
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error aborting request:', error);
        }
      }
    }
  }, [filteredMessages, markMessageasStopped, setIsSubmitting, setIsStreaming]);

  const handleSubmissionError = useCallback((
    error: any,
    currentInput: string,
    userMessageId: string,
    aiMessageId: string,
    currentTextFiles: any[],
    currentImageFiles: any[],
    currentQuotedContent: string
  ) => {
    removeMessage(userMessageId);
    removeMessage(aiMessageId);
    restoreInput(currentInput);
    setTextFiles(currentTextFiles);
    setImageFiles(currentImageFiles);
    setQuotedContent(currentQuotedContent);
  }, [removeMessage, restoreInput, setTextFiles, setImageFiles, setQuotedContent]);

  const prepareMessageContent = useCallback((messageInput: string, isEdit: boolean, explicitQuoted?: string) => {
    const q =
      explicitQuoted !== undefined ? explicitQuoted : (quotedContent || '');

    let userMessageContent = messageInput;

    if (q.trim().length > 0) {
      const quotedContentForMessage = '<quote>' + q + '</quote>';
      userMessageContent = quotedContentForMessage + '\n' + messageInput;
    }
    
    // Add resource viewing tag if user is viewing a resource and not in edit mode
    if (!isEdit && expandedResource && isResourcesVisible && !isTextAttachmentViewOpen) {
      const resourceViewingTag = "<resource_viewing>" + expandedResource.title + "</resource_viewing>\n";
      userMessageContent = resourceViewingTag + userMessageContent;
    }

    return userMessageContent;
  }, [quotedContent, expandedResource, isResourcesVisible, isTextAttachmentViewOpen]);

  const stripResourceViewingTag = useCallback((content: string) => {
    return content.replace(/<resource_viewing>.*?<\/resource_viewing>\n?/gs, '');
  }, []);

  const createNewChatIfNeeded = useCallback(async (editedMessage: any) => {
    let chatIdToUse = currentChatId || null;

    // Incognito: never create a Convex chat. Use a sentinel id so any
    // server-side validation that expects a non-empty chatId still passes,
    // even though the incognito endpoint ignores it.
    if (isIncognito) {
      return INCOGNITO_CHAT_ID;
    }

    if (!editedMessage && auth.user && (!chatIdToUse || chatIdToUse === 'new' || chatIdToUse === 'undefined')) {
      if (!isSpaceChat) {
        try {
          const newChat = await addNewChat();

          localStorage.removeItem('draft_new');
          localStorage.removeItem(`draft_${newChat.id}`);
          chatIdToUse = newChat.id;

          // Track chat creation
          trackChatCreated({
            chatId: newChat.id,
            source: 'first_message'
          });

          // Batch navigation and state updates
          Promise.resolve().then(() => {
            navigate(`/${newChat.id}`, { replace: true });
            updateMessageChatId(newChat.id);
          });
        } catch (err) {
          throw err;
        }
      }
    }

    return chatIdToUse;
  }, [currentChatId, auth.user, isSpaceChat, addNewChat, navigate, updateMessageChatId, trackChatCreated, isIncognito]);

  const insertMessages = useCallback(async (userContent: string, isEdit: boolean, editedMessage: any | undefined, tempUserMessageId: string | undefined, tempAiMessageId: string | undefined, editMode: Mode | undefined, newImages: MessageImage[], newTextAttachments: MessageDocument[]) => {
    let newBranchId: number = globalBranchCount || 1;

    if (isEdit && editedMessage) {
      newBranchId = maxBranchId + 1;
      const modeToUse = editMode || mode;
      
      await Promise.all([
        insertNewMessage({
          isAi: false,
          messagePairId: editedMessage.messagePairId,
          branchId: newBranchId,
          _id: tempUserMessageId!,
          content: userContent,
          mode: modeToUse,
          textAttachments: newTextAttachments,
          images: newImages
        }),
        insertNewMessage({
          isAi: true,
          messagePairId: editedMessage.messagePairId,
          branchId: newBranchId,
          _id: tempAiMessageId!,
          content: '',
          mode: modeToUse
        })
      ]);
      
      setGlobalBranchCount(newBranchId);
    } else {
      const tempMessagePairId = generateTempId();
      // For new chats, always use branch 1
      const branchIdToUse =
        !currentChatId || currentChatId === 'new'
          ? 1
          : globalBranchCount || 1;
      
      // Insert both messages at once to avoid multiple renders
      await Promise.all([
        insertNewMessage({
          isAi: false,
          messagePairId: tempMessagePairId,
          branchId: branchIdToUse,
          _id: tempUserMessageId!,
          content: userContent,
          mode: mode,
          textAttachments: newTextAttachments,
          images: newImages
        }),
        insertNewMessage({
          isAi: true,
          messagePairId: tempMessagePairId,
          branchId: branchIdToUse,
          _id: tempAiMessageId!,
          content: '',
          mode: mode
        })
      ]);
    }

    return newBranchId;
  }, [globalBranchCount, maxBranchId, insertNewMessage, setGlobalBranchCount, mode, currentChatId, generateTempId]);


  const handleSubmit = useCallback(async (e: SubmitEvent) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    auth.checkUsageData();

    const submittedImages = e.editMessage ? e.editImages ?? e.editMessage.images ?? [] : imageFiles;
    if (!checkModelCompatibility(!!submittedImages?.length, useWeb, messagesHaveImages)) {
      return;
    }
    
    // Determine requested model
    let requestedModel: requestModel = { id: currentModel.id };
    if (e.editMessage && e.editModel) {
      requestedModel = { id: e.editModel.id };
    }

    // Check if already submitting using ChatContext state
    if (isGeneratingResponse) {
      try {
        handleStopStreaming();
      } catch (error) {
        return;
      }
    }

    let tempAiMessageId = generateTempId();

    // Set submitting state immediately for local-first behavior
    setIsSubmitting(true);
    checkMobile();

    try {
      const editedMessage = e.editMessage;
      const currentInput =
        editedMessage && e?.editContent
          ? e.editContent
          : e.overrideInput !== undefined
            ? e.overrideInput
            : input;

      if (
        (!editedMessage &&
          (!currentInput.trim() && !textFiles?.length && !imageFiles?.length)) ||
        auth.isLoading
      ) {
        return;
      }

      const tempQuotedContent = editedMessage
        ? quotedContent || ''
        : e.overrideQuotedContent !== undefined
          ? e.overrideQuotedContent
          : quotedContent || '';

      if (!editedMessage) {
        // Clear the sent text and its undo history now. A new draft typed while
        // the response streams must survive completion.
        resetHistory();
        setQuotedContent('');
        setTextFiles([]);
        setImageFiles([]);
      }

      // Handle new chat creation and navigation FIRST, before any message operations
      const chatIdToUse = await createNewChatIfNeeded(editedMessage);
      const tempUserMessageId = generateTempId();

      const tempTextFiles = [...(textFiles || [])];
      const tempImageFiles = [...(imageFiles || [])];

      const quoteArgForPrepare = editedMessage
        ? undefined
        : e.overrideQuotedContent !== undefined
          ? e.overrideQuotedContent
          : undefined;

      const userMessageContent = prepareMessageContent(
        currentInput,
        !!editedMessage,
        quoteArgForPrepare
      );
      const localUserMessageContent = stripResourceViewingTag(userMessageContent);

    // Any text file created in-editor (or otherwise not yet uploaded) has an
    // empty url. Upload those on-send so the backend always receives a url.
    // Any failure is fatal — we'd rather abort the send than dispatch a
    // message with an inaccessible attachment.
    const ensureTextFilesUploaded = async (files: TextFileRecord[]): Promise<TextFileRecord[]> => {
      return Promise.all(files.map(async (file) => {
        if (file.url) return file;
        const mime =
          file.type === 'pdf' ? 'application/pdf'
          : file.type === 'code' ? 'text/plain'
          : 'text/markdown';
        const asFile = new File([file.content ?? ''], file.name, { type: mime });
        const res = await uploadDocument(asFile);
        return { ...file, url: res.file.url };
      }));
    };

    let uploadedTextFiles: TextFileRecord[];
    try {
      uploadedTextFiles = await ensureTextFilesUploaded(textFiles || []);
    } catch (err: any) {
      console.error('Failed to upload text attachments:', err);
      toast(
        err?.message
          ? `Couldn't upload attachment: ${err.message}`
          : "Couldn't upload one of your attachments. Please try again.",
        'error'
      );
      // No messages inserted yet, so just restore composer state.
      restoreInput(currentInput);
      setQuotedContent(tempQuotedContent);
      setTextFiles(tempTextFiles);
      setImageFiles(tempImageFiles);
      return;
    }

    const shouldForwardContent = (file: TextFileRecord): boolean =>
      Boolean(file.content) && file.type !== 'pdf';

    const newTextAttachmentsForMessage: MessageDocument[] = [];

    if (!editedMessage) {
        uploadedTextFiles.forEach(file => {
            newTextAttachmentsForMessage.push({
                title: file.name,
                url: file.url,
                type: file.type as DocumentType,
                ...(shouldForwardContent(file) ? { content: file.content! } : {})
            });
        });
    }

    const imageAttachmentsFiles: MessageImage[] = imageFiles?.map((image) => ({
      title: image.name,
      url: image.url,
      media_type: image.type as ImageType
    })) || [];

    const textAttachmentsArray: MessageDocument[] = uploadedTextFiles.map((file) => ({
      title: file.name,
      url: file.url,
      type: file.type,
      ...(shouldForwardContent(file) ? { content: file.content! } : {})
    }));

    const editImages = editedMessage ? e.editImages ?? editedMessage.images ?? [] : [];
    const editTextAttachments = editedMessage ? e.editTextAttachments ?? editedMessage.textAttachments ?? [] : [];

    const newBranchId = editedMessage
      ? await insertMessages(localUserMessageContent, true, editedMessage, tempUserMessageId, tempAiMessageId, e.editMode, editImages, editTextAttachments)
      : await insertMessages(localUserMessageContent, false, undefined, tempUserMessageId, tempAiMessageId, e.editMode, imageAttachmentsFiles, newTextAttachmentsForMessage);

    try {
      const oldContext: ClientMessage[] = convexMessagesToClientMessages(filteredMessages);

      const newClientUserMessage: ClientMessage = {
          id: tempUserMessageId,
          isAi: false,
          content: localUserMessageContent,
          timestamp: Date.now(),
          images: editedMessage ? editImages : imageAttachmentsFiles,
          textAttachments: editedMessage ? editTextAttachments : newTextAttachmentsForMessage,
          branchId: newBranchId,
          tool_calls: null,
          tool_results: null,
      };
      const upToDateContext = editedMessage
        ? [...oldContext.slice(0, -2), newClientUserMessage]
        : [...oldContext, newClientUserMessage];
        
      const controller = new AbortController();
      abortControllerRef.current = controller;
      streamChatIdRef.current = chatIdToUse;

      let inputToSubmit = userMessageContent;
      let stream: any;
      const startTime = Date.now();
      
      if (editedMessage) {
        const modeToUse = e.editMode || mode;
        
        // Track message edit
        trackMessageEdit({
          chatId: chatIdToUse || undefined,
          messageId: editedMessage._id,
          mode: modeToUse.name || modeToUse.id
        });
        
        startThinking();
        if (isLocalOnlyChat) {
          stream = await sendLocalOnlyMessage({
            content: localUserMessageContent,
            mode: modeToUse,
            context: contextBeforePair(editedMessage.messagePairId),
            imageFiles: editImages,
            textFiles: editTextAttachments,
            requestedModel,
            signal: controller.signal
          });
        } else {
          ({ stream } = await editMessage({
            chatId: chatIdToUse || '',
            messageId: editedMessage._id,
            content: currentInput,
            mode: modeToUse,
            globalBranchCount: newBranchId,
            useReasoning,
            useWeb,
            webCapable,
            resourceCapable,
            requestedModel,
            context: upToDateContext,
            signal: controller.signal,
            imageFiles: editImages,
            textFiles: editTextAttachments,
          }));
        }
      } else {
        // Track message sent
        const attachmentTypes: string[] = [];
        if (textFiles?.length) {
          attachmentTypes.push(...textFiles.map(f => f.type || 'doc'));
        }
        if (imageFiles?.length) {
          attachmentTypes.push(...imageFiles.map(f => f.type || 'image'));
        }
        
        // Check if reasoning is actually being used (model has isReasoning AND toggle is on)
        const isUsingReasoning = currentModel.isReasoning === true;
        
        trackMessageSent({
          chatId: chatIdToUse || undefined,
          mode: mode.name || mode.id,
          hasAttachments: (textFiles?.length || 0) > 0 || (imageFiles?.length || 0) > 0,
          attachmentTypes,
          messageLength: currentInput.length,
          isEdit: false,
          isRegenerate: false,
          modelId: requestedModel?.id,
          useReasoning: isUsingReasoning
        });
        
        if (isLocalOnlyChat) {
          if (auth.user) startThinking();
          stream = await sendLocalOnlyMessage({
            content: inputToSubmit,
            mode,
            context: oldContext,
            imageFiles: imageAttachmentsFiles,
            textFiles: textAttachmentsArray,
            requestedModel,
            signal: controller.signal
          });
        } else {
          startThinking();
          ({ stream } = await sendMessage({
            chatId: chatIdToUse || '',
            content: inputToSubmit,
            mode,
            abortSignal: controller.signal,
            imageFiles: imageAttachmentsFiles,
            textFiles: textAttachmentsArray,
            globalBranchCount: newBranchId,
            useReasoning,
            useWeb,
            webCapable,
            resourceCapable,
            requestedModel,
            context: upToDateContext
          }));
          setUseWeb(false);
        }
      }

      scrollToBottomGenerous();
      
      await processStream(stream, tempAiMessageId, tempUserMessageId, (newAiId: string) => {
        tempAiMessageId = newAiId;
      });
      
      // Track message received
      const duration = Date.now() - startTime;
      trackMessageReceived({
        chatId: chatIdToUse || undefined,
        mode: mode.name || mode.id,
        responseLength: 0, // Will be calculated from stream
        duration,
        modelId: requestedModel?.id,
        wasStoppedEarly: false
      });

      parseAndAddTextAttachments(textAttachmentsArray, chatIdToUse || undefined);

      tempTextFiles.forEach((file) => {
        localStorage.removeItem(`file_${file.name}`);
      });
    } catch (error: any) {
      // Don't show error for aborted requests (user cancelled)
      if (error.name !== 'AbortError') {
        if (isMessageLimitError(error)) {
          handleDailyLimitError();
        } else {
          toast(error.message || 'Something went wrong', 'error');
          stopGeneration(tempAiMessageId);
        }
        handleSubmissionError(error, currentInput, tempUserMessageId, tempAiMessageId, tempTextFiles, tempImageFiles, tempQuotedContent);
      }
      
    } finally {
      resetThinking();
      abortControllerRef.current = null;
      streamChatIdRef.current = null;

      if(!auth.user){
        stopGeneration(tempAiMessageId)
      }
      
      setIsStreaming(false);

      if (isMountedRef.current) {
        setTimeout(() => {
          focusInput();
        }, 100);
      }
    }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    auth,
    filteredMessages,
    messagesHaveImages,
    handleSubmissionError,
    handleDailyLimitError,
    parseAndAddTextAttachments,
    imageFiles,
    textFiles,
    setTextFiles,
    setImageFiles,
    input,
    focusInput,
    processStream,
    resetThinking,
    handleStopStreaming,
    resetHistory,
    checkMobile,
    scrollToBottomGenerous,
    useReasoning,
    useWeb,
    setUseWeb,
    webCapable,
    resourceCapable,
    startThinking,
    stopGeneration,
    mode,
    checkModelCompatibility,
    currentModel,
    quotedContent,
    setQuotedContent,
    setIsSubmitting,
    isGeneratingResponse,
    generateTempId,
    toast,
    prepareMessageContent,
    stripResourceViewingTag,
    createNewChatIfNeeded,
    insertMessages,
    setIsStreaming,
    trackMessageSent,
    trackMessageReceived,
    trackMessageEdit,
    restoreInput,
    isLocalOnlyChat,
    sendLocalOnlyMessage,
    contextBeforePair
  ]);

  return {
    handleSubmit,
    handleStopStreaming,
    regenerateMessage,
    answerQuestion,
    abortControllerRef
  };
};
